import { call } from '@advanza/api'
import { SelectCheckbox, type SelectCheckboxOption } from '@advanza/input'
import { useToast } from 'components/misc/Toaster'
import { useEffect, useState } from 'react'

interface GetServicesCrossSellOptionsResponse {
    availableOptions: {
        optionId: number
        optionTitle: string
    }[]
    selectedOptionIds: number[]
}

interface Props {
    serviceId: number
}

const CrossSellOptionsDropdown = ({ serviceId }: Props) => {
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [availableOptions, setAvailableOptions] = useState<SelectCheckboxOption<number>[]>([])
    const [selectedOptionIds, setSelectedOptionIds] = useState<number[]>([])
    const { addToast } = useToast()

    const showErrorToast = (action: string, error: unknown) => {
        const details = (() => {
            if (typeof error === 'string') return error
            if (error instanceof Error) return error.message
            if (error && typeof error === 'object' && 'error' in error) {
                return String((error as { error: unknown }).error)
            }
            return 'Unknown error'
        })()

        addToast({ msg: `${action} cross-sell options failed: ${details}` })
    }

    const onChange = (values: number[]) => {
        setSelectedOptionIds(values)

        // automatically save the changes
        setIsSaving(true)
        call(`office/services/saveServicesCrossSellOptions/${serviceId}`, {
            json: {
                optionIds: values,
            },
        })
            .then(null, (error) => showErrorToast('Saving', error))
            .finally(() => {
                setIsSaving(false)
            })
    }

    useEffect(() => {
        setIsLoading(true)
        call(`office/services/getServicesCrossSellOptions/${serviceId}`)
            .then(
                (response: GetServicesCrossSellOptionsResponse) => {
                    setAvailableOptions(
                        response.availableOptions.map((option) => ({
                            value: option.optionId,
                            name: option.optionTitle,
                        })),
                    )
                    setSelectedOptionIds(response.selectedOptionIds)
                },
                (error) => showErrorToast('Fetching', error),
            )
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    const placeholder = (() => {
        if (isLoading) return 'Loading...'
        if (isSaving) return 'Saving...'
        return 'Select options'
    })()

    return (
        <div>
            <div className="font-bold mb-2">
                Display cross-sell directly after conversion if user selects:
            </div>
            <SelectCheckbox
                placeholder={placeholder}
                options={availableOptions}
                multiple
                value={selectedOptionIds}
                onChangeValue={onChange}
                wide
            />
        </div>
    )
}

export default CrossSellOptionsDropdown
